/**
 * =============================================================================
 * COMPONENT:  AuctionImport.js
 * FUNKTION:   Auktionsdaten per Copy-Paste importieren, Verlauf + Diff
 * VERSION:    v2.0
 * DATUM:      2026-03-05
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import AdsDecisions from './AdsDecisions';
import './imports-shared.css';
import './AuctionImport.css';

const API = '/api/ads_auction_import.php';

const COLS = [
    { key: 'impression_share',    label: 'IS %' },
    { key: 'overlap_rate',        label: 'Überschneidung' },
    { key: 'position_above_rate', label: 'Pos. oben' },
    { key: 'top_of_page_rate',    label: 'Oben %' },
    { key: 'abs_top_rate',        label: 'Abs. oben %' },
    { key: 'outranking_share',    label: 'Überbietung' },
];

function fmt(val) {
    return val != null ? val + '%' : '—';
}

function fmtDiff(cur, prev) {
    if (cur == null || prev == null) return null;
    var d = (parseFloat(cur) - parseFloat(prev)).toFixed(1);
    if (d === '0.0') return null;
    return d > 0 ? '+' + d : '' + d;
}

export default function AuctionImport({ campaignId, campaignName, selectedIds, campaignList, von1, bis1 }) {
    var [data, setData]           = useState([]);
    var [collapsedPeriods, setCollapsedPeriods] = useState({});
    var [loading, setLoading]     = useState(false);
    var [showForm, setShowForm]   = useState(false);
    var [importing, setImporting] = useState(false);
    var [preview, setPreview]     = useState(null);
    var [importMsg, setImportMsg] = useState(null);
    var [datumVon, setDatumVon]   = useState(von1 || '');
    var [datumBis, setDatumBis]   = useState(bis1 || '');
    var [rawText, setRawText]     = useState('');
    // Explizite Kampagnen-Wahl im Import-Form (verhindert "selected[0]"-Bug)
    var [importCampId, setImportCampId] = useState(String(campaignId || ''));

    var token = localStorage.getItem('token') || '';

    var load = useCallback(function() {
        if (!campaignId) return;
        setLoading(true);
        console.log('[Auction] load campaignId:', campaignId);
        setData([]);
        fetch(API + '?campaign_id=' + campaignId + '&limit=100', {
            headers: { Authorization: 'Bearer ' + token }
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            console.log('[Auction] load response:', json);
            if (json.success) setData(json.data || []);
            setLoading(false);
        })
        .catch(function(e) { console.error('[Auction] load error:', e); setLoading(false); });
    }, [campaignId, token]);

    useEffect(function() { load(); }, [load]);
    useEffect(function() { setDatumVon(von1 || ''); setDatumBis(bis1 || ''); }, [von1, bis1]);

    // Aktuell gewählte Kampagne aus campaignList holen
    function getCurrentCamp() {
        var c = campaignList && campaignList.find(function(c) { return String(c.campaign_id) === String(importCampId); });
        return c || { campaign_id: importCampId, campaign_name: campaignName || '?' };
    }

    function handlePreview() {
        if (!rawText || !datumVon || !datumBis || !importCampId) return;
        setImporting(true);
        setImportMsg(null);
        setPreview(null);
        var c = getCurrentCamp();
        fetch(API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify({
                preview: 1, raw: rawText,
                datum_von: datumVon, datum_bis: datumBis,
                campaign_id: c.campaign_id, campaign_name: c.campaign_name,
            }),
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) setPreview(json.rows || []);
            else setImportMsg({ ok: false, text: json.message });
            setImporting(false);
        })
        .catch(function(e) { setImportMsg({ ok: false, text: e.message }); setImporting(false); });
    }

    function handleSave() {
        if (!preview) return;
        setImporting(true);
        var c = getCurrentCamp();
        fetch(API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify({
                save: 1, raw: rawText,
                datum_von: datumVon, datum_bis: datumBis,
                campaign_id: c.campaign_id, campaign_name: c.campaign_name,
            }),
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) {
                setImportMsg({ ok: true, text: json.imported + ' Einträge erfasst' });
                setPreview(null);
                setRawText('');
                setShowForm(false);
                load();
            } else {
                setImportMsg({ ok: false, text: json.message });
            }
            setImporting(false);
        })
        .catch(function(e) { setImportMsg({ ok: false, text: e.message }); setImporting(false); });
    }

    console.log('[Auction] render data:', data.length, 'showForm:', showForm, 'loading:', loading);
    var periods = Array.from(new Set(data.map(function(d) { return d.datum_von + '__' + d.datum_bis; })));

    return (
        <div className="imp-wrap">
            <div className="imp-header">
                <span className="imp-title">&#x2694;&#xFE0F; Auktionsdaten</span>
                <button className="imp-btn imp-btn-import" onClick={function() { setShowForm(function(s) { return !s; }); setPreview(null); setImportMsg(null); }}>
                    {showForm ? '✕ Abbrechen' : '+ Importieren'}
                </button>
            </div>

            {showForm && (
                <div className="imp-form">
                    <p className="imp-hint">
                        Google Ads → Auktionsdaten → Zeitraum wählen → alle Zeilen kopieren → hier einfügen.
                    </p>
                    <div className="imp-form-row">
                        <div className="imp-field" style={{flex: 2}}>
                            <label>Kampagne (explizit wählen!)</label>
                            <select
                                value={importCampId}
                                onChange={function(e) { setImportCampId(e.target.value); }}
                                style={{padding: '6px 8px', border: '1px solid #c0c0c0', borderRadius: '3px', fontSize: '0.9rem'}}
                            >
                                <option value="">— Kampagne wählen —</option>
                                {(campaignList || []).map(function(c) {
                                    return <option key={c.campaign_id} value={c.campaign_id}>{c.campaign_name}</option>;
                                })}
                            </select>
                        </div>
                        <div className="imp-field">
                            <label>Von</label>
                            <input type="date" value={datumVon} onChange={function(e) { setDatumVon(e.target.value); }} />
                        </div>
                        <div className="imp-field">
                            <label>Bis</label>
                            <input type="date" value={datumBis} onChange={function(e) { setDatumBis(e.target.value); }} />
                        </div>
                    </div>
                    <div className="imp-field">
                        <label>Daten einfügen (Tab-getrennt aus Google Ads)</label>
                        <textarea rows={8} placeholder="Hier einfügen…"
                            value={rawText} onChange={function(e) { setRawText(e.target.value); }} />
                    </div>
                    <div className="imp-form-actions">
                        <button className="imp-btn imp-btn-preview" onClick={handlePreview} disabled={importing || !rawText || !importCampId}>
                            {importing && !preview ? 'Prüfe…' : '🔍 Vorschau'}
                        </button>
                        {preview && (
                            <button className="imp-btn imp-btn-save" onClick={handleSave} disabled={importing}>
                                {importing ? 'Speichere…' : '💾 Erfassen (' + preview.length + ' Einträge)'}
                            </button>
                        )}
                        {importMsg && (
                            <span className={importMsg.ok ? 'imp-msg-ok' : 'imp-msg-err'}>
                                {importMsg.ok ? '✅ ' : '❌ '}{importMsg.text}
                            </span>
                        )}
                    </div>

                    {preview && preview.length > 0 && (
                        <div className="imp-preview-wrap">
                            <div className="imp-preview-title">Vorschau — {preview.length} Einträge</div>
                            <div className="imp-table-wrap">
                                <table className="imp-table">
                                    <thead>
                                        <tr>
                                            <th>Domain</th>
                                            {COLS.map(function(c) { return <th key={c.key}>{c.label}</th>; })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.map(function(r, i) {
                                            return (
                                                <tr key={i} className={r.is_self ? 'imp-row-self' : 'imp-row'}>
                                                    <td className="ai-domain">{r.is_self ? '👤 Sie' : r.domain}</td>
                                                    {COLS.map(function(c) {
                                                        return <td key={c.key} className="ai-val">{fmt(r[c.key])}</td>;
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {loading && <p className="imp-loading">Lädt…</p>}
            {!loading && data.length === 0 && (
                <p className="imp-empty">Noch keine Auktionsdaten. "+ Importieren" um zu starten.</p>
            )}

            {!loading && periods.map(function(period, pidx) {
                var parts    = period.split('__');
                var dVon     = parts[0];
                var dBis     = parts[1];
                var rows     = data.filter(function(d) { return d.datum_von === dVon && d.datum_bis === dBis; });
                var self     = rows.find(function(r) { return String(r.is_self) === '1'; });
                var comps    = rows.filter(function(r) { return String(r.is_self) !== '1'; })
                    .sort(function(a, b) { return (parseFloat(b.impression_share) || 0) - (parseFloat(a.impression_share) || 0); });

                var prevPeriod = periods[pidx + 1];
                var prevRows   = prevPeriod
                    ? data.filter(function(d) { return d.datum_von + '__' + d.datum_bis === prevPeriod; })
                    : [];

                function getPrevVal(domain, col) {
                    var found = prevRows.find(function(p) { return p.domain === domain; });
                    return found ? found[col] : null;
                }

                function renderDiff(cur, prevVal, invertColor) {
                    var diff = fmtDiff(cur, prevVal);
                    if (!diff) return null;
                    var positive = parseFloat(diff) > 0;
                    var good = invertColor ? !positive : positive;
                    return <span className={'ai-diff ' + (good ? 'imp-diff-good' : 'imp-diff-bad')}>{diff}</span>;
                }

                return (
                    <div key={period} className="imp-period">
                        <div
                            className="imp-period-header imp-period-toggle"
                            onClick={function() { setCollapsedPeriods(function(p) { return {...p, [period]: p[period] === false ? true : false}; }); }}
                            style={{cursor:'pointer'}}
                        >
                            <span>{collapsedPeriods[period] === false ? '▼' : '▶'} 📅 {dVon} – {dBis}</span>
                            {self && (
                                <span className="imp-self-is">
                                    Eigener IS: <strong>{fmt(self.impression_share)}</strong>
                                </span>
                            )}
                        </div>
                        {collapsedPeriods[period] === false && (
                            <div className="imp-table-wrap">
                                <table className="imp-table">
                                    <thead>
                                        <tr>
                                            <th>Domain</th>
                                            {COLS.map(function(c) { return <th key={c.key}>{c.label}</th>; })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {self && (
                                            <tr className="imp-row-self">
                                                <td className="ai-domain">👤 Sie</td>
                                                {COLS.map(function(c) {
                                                    return (
                                                        <td key={c.key} className="ai-val">
                                                            {fmt(self[c.key])}
                                                            {renderDiff(self[c.key], getPrevVal('Sie', c.key), false)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        )}
                                        {comps.map(function(r, i) {
                                            return (
                                                <tr key={i} className="imp-row">
                                                    <td className="ai-domain">{r.domain}</td>
                                                    {COLS.map(function(c) {
                                                        var val     = r[c.key];
                                                        var selfVal = self ? self[c.key] : null;
                                                        var worse   = val != null && selfVal != null && parseFloat(val) > parseFloat(selfVal);
                                                        var better  = val != null && selfVal != null && parseFloat(val) < parseFloat(selfVal);
                                                        return (
                                                            <td key={c.key} className={'ai-val ' + (worse ? 'imp-worse' : better ? 'imp-better' : '')}>
                                                                {fmt(val)}
                                                                {renderDiff(val, getPrevVal(r.domain, c.key), true)}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}

            {data.length > 0 && (
                <AdsDecisions
                    bereich="auktionen"
                    selectedIds={selectedIds}
                    campaignList={campaignList}
                />
            )}
        </div>
    );
}